import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Instagram, Linkedin, Mail, MapPin, ArrowRight } from "lucide-react";
import { NAV_LINKS } from "@/data/content";
import { CONTACT_EMAIL, CONTACT_LOCATION, SOCIAL_LINKS } from "@/constants/contact";

const SOCIAL_ICONS = { Instagram, LinkedIn: Linkedin };

const Footer = () => {
  const [email, setEmail] = useState("");

  const subscribe = (e) => {
    e.preventDefault();
    if (!email.includes("@")) return toast.error("Please enter a valid email address.");
    toast.success("You're on the list. Welcome to the IBY community!");
    setEmail("");
  };

  return (
    <footer className="relative bg-[#0B132B] text-white grain overflow-hidden" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-24 pb-10 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 pb-16 border-b border-white/10">
          <div className="lg:col-span-5">
            <p className="font-display font-bold text-3xl mb-4">
              IBY<span className="text-[#FF5A36]">.</span>
            </p>
            <p className="font-display text-2xl md:text-3xl font-medium leading-snug text-white/90 max-w-md">
              A world where opportunities are equal for every young person.
            </p>
            <div className="flex gap-3 mt-8">
              {SOCIAL_LINKS.map(({ label, href }) => {
                const Icon = SOCIAL_ICONS[label];
                return (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    data-testid={`footer-social-${label.toLowerCase()}`}
                    className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:bg-[#FF5A36] hover:border-[#FF5A36] hover:text-white transition-colors duration-300"
                  >
                    <Icon size={18} />
                  </a>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-3">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#FFB323] mb-6">Explore</p>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.path}>
                  <Link to={l.path} className="text-white/70 hover:text-white transition-colors duration-300 text-sm font-medium">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#FFB323] mb-6">Stay Inspired</p>
            <p className="text-white/70 text-sm leading-relaxed mb-5">
              Student stories, mentor spotlights and program news — straight to your inbox, once a month.
            </p>
            <form onSubmit={subscribe} className="flex gap-2" data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                data-testid="newsletter-email-input"
                className="flex-1 bg-white/10 border border-white/15 rounded-full px-5 py-3 text-sm placeholder:text-white/40 focus:outline-none focus:border-[#FF5A36] transition-colors"
              />
              <button
                type="submit"
                data-testid="newsletter-submit-btn"
                aria-label="Subscribe"
                className="w-12 h-12 rounded-full bg-[#FF5A36] flex items-center justify-center hover:bg-[#E04825] transition-colors duration-300 shrink-0"
              >
                <ArrowRight size={18} />
              </button>
            </form>
            <div className="mt-8 space-y-2 text-sm text-white/60">
              <p className="flex items-center gap-2">
                <Mail size={15} className="text-[#FF5A36]" />
                <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-white transition-colors duration-300">
                  {CONTACT_EMAIL}
                </a>
              </p>
              <p className="flex items-center gap-2">
                <MapPin size={15} className="text-[#FF5A36]" /> {CONTACT_LOCATION}
              </p>
            </div>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4 md:gap-8 text-xs text-white/40">
          <p className="md:flex-1 text-center md:text-left">
            © 2026 IBY — Inspired By You. All rights reserved.
          </p>
          <p className="md:flex-1 text-center">
            Designed and built by Deeptanshu Sharma
          </p>
          <p className="italic md:flex-1 text-center md:text-right">
            Real access — not just inspiration.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
